import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Filter } from "lucide-react";
import { Logo } from "../components/Brand";
import { useApp, API } from "../AppContext";
import { CURRENT_MODULE_CODE, CURRENT_MODULE_NAME } from "../config/progere-modules";
import ProgereTwoToneTitle from "../components/ProgereTwoToneTitle";
import { CATEGORY_META } from "../components/ProgereReleasePopup";
import ProgereLegalFooter from "../components/ProgereLegalFooter";

const List = ({ items, category, isFr }) => {
  if (!items || items.length === 0) return null;
  const meta = CATEGORY_META[category] || CATEGORY_META.new;
  const Icon = meta.Icon;
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded font-black text-[10px] uppercase"
          style={{
            background: `color-mix(in srgb, ${meta.color} 15%, transparent)`,
            color: meta.color,
            border: `1px solid color-mix(in srgb, ${meta.color} 38%, transparent)`,
          }}
        >
          <Icon size={11} /> {isFr ? meta.labelFr : meta.labelEn}
        </span>
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm pl-2" style={{ color: "var(--pg-text-secondary)" }}>• {it}</li>
        ))}
      </ul>
    </div>
  );
};

const pickList = (r, keyFr, keyEn, isFr) => {
  const p = isFr ? r?.[keyFr] : r?.[keyEn];
  if (Array.isArray(p) && p.length > 0) return p;
  const f = isFr ? r?.[keyEn] : r?.[keyFr];
  return Array.isArray(f) ? f : [];
};

export default function ReleasesPage() {
  const { lang, setLang } = useApp();
  const isFr = lang === "fr";
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/releases/history`, { params: { module_code: CURRENT_MODULE_CODE } })
      .then(({ data }) => { if (!cancelled) { setEntries(data.entries || []); setLoading(false); } })
      .catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((r) => {
      if (typeFilter !== "all" && r.release_type !== typeFilter) return false;
      if (!q.trim()) return true;
      const hay = [r.title_fr, r.title_en, r.version, ...(r.highlights_fr || []), ...(r.fixes_fr || [])].join(" ").toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [entries, q, typeFilter]);

  const T = isFr ? {
    title: "Notes de version",
    tagline: "HISTORIQUE",
    subtitle: "Toutes les mises à jour de PROGÈRE",
    search: "Rechercher…",
    all: "Toutes",
    patch: "Correctif",
    minor: "Mineur",
    major: "Majeur",
    security: "Sécurité",
    empty: "Aucune version publiée pour l'instant.",
    home: "Accueil",
    published: "Publié le",
  } : {
    title: "Release notes",
    tagline: "HISTORY",
    subtitle: "All updates to PROGÈRE",
    search: "Search…",
    all: "All",
    patch: "Patch",
    minor: "Minor",
    major: "Major",
    security: "Security",
    empty: "No release published yet.",
    home: "Home",
    published: "Published on",
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pg-bg-main)" }} data-testid="page-releases">
      <header className="sticky top-0 z-40" style={{ background: "var(--pg-topbar)", borderBottom: "1px solid var(--pg-border)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/"><Logo size="sm" /></Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(isFr ? "en" : "fr")}
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ border: "1px solid var(--pg-border-strong)", color: "var(--pg-text-secondary)" }}
            >
              {isFr ? "EN" : "FR"}
            </button>
            <Link to="/" className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" style={{ color: "var(--pg-text-secondary)" }}>
              <ArrowLeft size={12} /> {T.home}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: "var(--pg-gold)" }}>
            {T.tagline}
          </div>
          <ProgereTwoToneTitle firstPart="PROGÈRE" secondPart={CURRENT_MODULE_NAME} size="text-3xl sm:text-4xl" />
          <p className="mt-2 text-sm" style={{ color: "var(--pg-text-secondary)" }}>{T.subtitle}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--pg-text-muted)" }} />
              <input
                data-testid="releases-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={T.search}
                className="progere-input w-full pl-9"
              />
            </div>
            <select
              data-testid="releases-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="progere-select"
            >
              <option value="all">{T.all}</option>
              <option value="major">{T.major}</option>
              <option value="minor">{T.minor}</option>
              <option value="patch">{T.patch}</option>
              <option value="security">{T.security}</option>
            </select>
          </div>

          {loading ? (
            <div className="mt-10 text-center text-sm" style={{ color: "var(--pg-text-muted)" }}>…</div>
          ) : filtered.length === 0 ? (
            <div className="mt-10 rounded-xl p-8 text-center" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)", color: "var(--pg-text-muted)" }}>
              {T.empty}
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              {filtered.map((r, idx) => {
                const isLatest = idx === 0;
                const highlights = pickList(r, "highlights_fr", "highlights_en", isFr);
                const improvements = pickList(r, "improvements_fr", "improvements_en", isFr);
                const fixes = pickList(r, "fixes_fr", "fixes_en", isFr);
                const security = pickList(r, "security_updates_fr", "security_updates_en", isFr);
                const publishedAt = r.published_at ? new Date(r.published_at).toLocaleDateString(isFr ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "";
                const title = (isFr ? r.title_fr : r.title_en) || r.title_fr;
                return (
                  <article
                    key={r.id}
                    data-testid={`release-card-${r.version}`}
                    className="rounded-2xl p-6"
                    style={{
                      background: "var(--pg-bg-card)",
                      border: `1px solid ${isLatest ? "color-mix(in srgb, var(--pg-gold) 55%, var(--pg-border))" : "var(--pg-border)"}`,
                      boxShadow: isLatest ? "0 12px 40px rgba(242,188,46,0.08)" : "var(--pg-shadow)",
                    }}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded font-black text-[11px]"
                        style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
                      >
                        v{r.version}
                      </span>
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded"
                        style={{ background: "var(--pg-bg-tertiary)", color: "var(--pg-text-muted)", border: "1px solid var(--pg-border)" }}>
                        {r.release_type}
                      </span>
                      {isLatest && (
                        <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded" style={{ background: "var(--pg-success)", color: "#fff" }}>
                          {isFr ? "COURANTE" : "CURRENT"}
                        </span>
                      )}
                      {publishedAt && (
                        <span className="text-xs" style={{ color: "var(--pg-text-muted)" }}>
                          {T.published} {publishedAt}
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading text-xl font-bold" style={{ color: "var(--pg-text-primary)" }}>{title}</h2>
                    <List items={highlights} category="new" isFr={isFr} />
                    <List items={improvements} category="improvement" isFr={isFr} />
                    <List items={fixes} category="fix" isFr={isFr} />
                    <List items={security} category="security" isFr={isFr} />
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <ProgereLegalFooter />
    </div>
  );
}
